import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    IconButton,
    CircularProgress,
    Tooltip
} from '@mui/material';
import {
    ArrowBackIos,
    ArrowForwardIos,
    Favorite,
    AccessTime,
    DirectionsWalk,
    RemoveRedEye
} from '@mui/icons-material';
import {
    Cell,
    BarChart,
    XAxis,
    YAxis,
    Bar,
    ReferenceLine,
    ReferenceArea,
    ResponsiveContainer,
    LineChart,
    Line
} from 'recharts';
import dayjs from 'dayjs';
import { useNavigate, useLocation } from 'react-router-dom';
import { t } from 'i18next';
import { useRecordANN, useRecordHr } from '../../../../hooks/useRecords';
import { fetchUser } from '../../../../services/users';

const zoneColors = ['#aae692', '#2c9e00', '#f8eeb8', '#e6c72e', '#f3a7a1', '#f44336'];

const COLORS = [
    '#2196f3',
    '#f44336',
    '#4caf50',
    '#ff9800',
    '#9c27b0',
    '#00bcd4',
    '#795548',
    '#607d8b'
];

const EventDetailsCard = ({
    event,
    onClose,
    onPrev,
    onNext,
    selected,
    expanded,
    selectedPlan
}) => {
    const [userData, setUserData] = useState([]);
    const navigate = useNavigate();
    const location = useLocation();
    const roleUuid = event?.patient?.role_uuid || '';
    const isExercise = event.type === 'EXERCISE';

    const TYPE_LABELS = {
        rehabilitation: t('exercise.types.rehabilitation'),
        treadmill: t('exercise.types.treadmill'),
        ergometer: t('exercise.types.ergometer'),
        walking: t('exercise.types.walking'),
        running: t('exercise.types.running'),
        cycling: t('exercise.types.cycling'),
        rest: t('exercise.types.rest'),
        easy: t('exercise.types.easy'),
        other: t('exercise.types.other'),
        squat: t('exercise.types.squat'),
        sit_to_stand: t('exercise.types.sit_to_stand'),
        push: t('exercise.types.push'),
        pull: t('exercise.types.pull'),
        arm_raise: t('exercise.types.arm_raise'),
        knee_extension: t('exercise.types.knee_extension')
    };

    useEffect(() => {
        if (!roleUuid) return;
        const fetchData = async () => {
            try {
                const [userResp] = await Promise.all([fetchUser(roleUuid)]);
                setUserData(userResp.data?.user_data);
            } catch (error) {
                console.error('Erro ao procurar dados do paciente:', error);
            }
        };
        fetchData();
    }, [roleUuid]);

    const { hrMax, hrMin, dataHrZone, hrPoints, isLoading, error } = useRecordHr(
        event || {},
        userData
    );

    const hasAnn = event?.config?.dataTypes?.includes('ANN');
    const { data: annData } = useRecordANN(hasAnn ? event.uuid : null);

    const annotations =
        annData?.ts?.map((timestamp, i) => ({
            iso: timestamp,
            time: new Date(timestamp).toLocaleTimeString('pt-PT', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            }),
            label: annData.text[i],
            type: annData.type[i],
            value: annData.value[i]
        })) || [];

    const buildExerciseIntervals = (anns, hrPoints) => {
        if (!anns?.length || !hrPoints?.length) return [];
        const exercises = anns
            .filter((a) => a.type?.includes('E-EXERCISE'))
            .sort((a, b) => new Date(a.iso) - new Date(b.iso));

        if (!exercises.length) return [];

        const intervals = [];
        const firstIso = hrPoints[0]?.iso || event.ti;
        const firstTime = hrPoints[0]?.time;
        const lastIso = hrPoints[hrPoints.length - 1]?.iso || event.tf;
        const lastTime = hrPoints[hrPoints.length - 1]?.time;

        let currentStart = null;
        let currentLabel = null;

        for (const ann of exercises) {
            let state = null;
            try {
                state = ann.value ? JSON.parse(ann.value).state : null;
            } catch {
                state = null;
            }

            if (state === 'start') {
                currentStart = { iso: ann.iso, time: ann.time };
                currentLabel =
                    TYPE_LABELS[ann.label?.toLowerCase()] || ann.label || 'Exercício';
            } else if (state === 'stop') {
                const startIso = currentStart?.iso || firstIso;
                const startTime = currentStart?.time || firstTime;
                const translated =
                    TYPE_LABELS[currentLabel?.toLowerCase()] ||
                    TYPE_LABELS[ann.label?.toLowerCase()] ||
                    currentLabel ||
                    ann.label ||
                    'Exercício';
                intervals.push({
                    label: translated,
                    startIso,
                    startTime,
                    endIso: ann.iso,
                    endTime: ann.time
                });
                currentStart = null;
                currentLabel = null;
            }
        }

        if (currentStart) {
            intervals.push({
                label:
                    TYPE_LABELS[currentLabel?.toLowerCase()] ||
                    currentLabel ||
                    'Exercício',
                startIso: currentStart.iso,
                startTime: currentStart.time,
                endIso: lastIso,
                endTime: lastTime
            });
        }

        return intervals;
    };

    const exerciseIntervals = useMemo(
        () => buildExerciseIntervals(annotations, hrPoints),
        [annotations, hrPoints]
    );

    const timeToIndex = Object.fromEntries(hrPoints.map((p, i) => [p.time, i]));

    const labelColorMap = {};

    exerciseIntervals.forEach((interval) => {
        if (!labelColorMap[interval.label]) {
            labelColorMap[interval.label] =
                COLORS[Object.keys(labelColorMap).length % COLORS.length];
        }
    });

    const exerciseLegend = Object.entries(labelColorMap).map(([label, color]) => ({
        label,
        color
    }));

    if (!event) return null;

    if (isLoading) {
        return (
            <Card sx={{ mb: 3, borderRadius: 3, p: 4, textAlign: 'center' }}>
                <CircularProgress size={50} />
                <Typography variant="body2" sx={{ mt: 2 }}>
                    {t('exercise.loading')}
                </Typography>
            </Card>
        );
    }

    if (error) {
        return (
            <Card sx={{ mb: 3, borderRadius: 3, p: 4, textAlign: 'center' }}>
                <Typography color="error">{t('exercise.error')}</Typography>
            </Card>
        );
    }

    return (
        <Card sx={{ mb: 3, borderRadius: 3 }}>
            <CardContent sx={{ backgroundColor: '#f9f9f9', borderRadius: 3, p: 3 }}>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    position="relative"
                    mb={2}
                >
                    <Typography
                        variant="h4"
                        fontWeight="bold"
                        sx={{
                            color: '#1976d2',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '40%'
                        }}
                    >
                        {t('exercise.sessionTitle')}
                    </Typography>

                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)'
                        }}
                    >
                        <IconButton onClick={onNext}>
                            <ArrowBackIos color="primary" />
                        </IconButton>
                        <Typography variant="h6" fontWeight="bold" mx={1}>
                            {dayjs(event.ti).format('HH:mm')}
                        </Typography>
                        <IconButton onClick={onPrev}>
                            <ArrowForwardIos color="primary" />
                        </IconButton>
                    </Box>

                    <Tooltip title={t('exercise.tooltipViewDetails')}>
                        <IconButton
                            color="primary"
                            onClick={() =>
                                navigate('record', {
                                    state: {
                                        record: event,
                                        from: location,
                                        seeUserState: {
                                            selected,
                                            selectedPlan,
                                            expanded
                                        }
                                    }
                                })
                            }
                        >
                            <RemoveRedEye />
                        </IconButton>
                    </Tooltip>
                </Box>

                {isExercise ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <Card
                                elevation={3}
                                sx={{
                                    p: 2.5,
                                    borderRadius: 3,
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'flex-start',
                                    alignItems: 'stretch',
                                    gap: 3
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                    align="center"
                                    sx={{
                                        mb: 2,
                                        color: '#1976d2',
                                        position: 'relative',
                                        '&::after': {
                                            content: '""',
                                            display: 'block',
                                            width: '55px',
                                            height: '3px',
                                            backgroundColor: '#1976d2',
                                            borderRadius: '2px',
                                            margin: '6px auto 0'
                                        }
                                    }}
                                >
                                    {t('exercise.summary')}
                                </Typography>

                                <Grid
                                    container
                                    justifyContent="center"
                                    alignItems="center"
                                    sx={{ maxWidth: 600, mx: 'auto' }}
                                >
                                    <Grid item xs={12} sm={4}>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <Favorite color="primary" sx={{ mr: 1 }} />
                                            <Typography variant="body2">
                                                <strong>{t('exercise.hrMax')}:</strong>{' '}
                                                {hrMax} bpmm
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <AccessTime color="primary" sx={{ mr: 1 }} />
                                            <Typography variant="body2">
                                                <strong>{t('exercise.duration')}:</strong>{' '}
                                                {event.duration / 60} min
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <DirectionsWalk
                                                color="primary"
                                                sx={{ mr: 1 }}
                                            />
                                            <Typography variant="body2">
                                                <strong>{t('exercise.distance')}:</strong>{' '}
                                                {event.distance} km
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>

                                <Box sx={{ mt: 2 }}>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight="bold"
                                        align="center"
                                        sx={{
                                            mb: 2,
                                            color: '#1976d2',
                                            position: 'relative',
                                            '&::after': {
                                                content: '""',
                                                display: 'block',
                                                width: '225px',
                                                height: '3px',
                                                backgroundColor: '#1976d2',
                                                borderRadius: '2px',
                                                margin: '6px auto 0'
                                            }
                                        }}
                                    >
                                        {t('exercise.trainingZones')}
                                    </Typography>

                                    <Box sx={{ width: '100%', height: 220 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart
                                                data={dataHrZone?.summaryZones || []}
                                                layout="vertical"
                                                margin={{
                                                    top: 10,
                                                    right: 50,
                                                    bottom: 10
                                                }}
                                            >
                                                <XAxis
                                                    type="number"
                                                    tick={{ fontSize: 12 }}
                                                />
                                                <YAxis
                                                    dataKey="label"
                                                    type="category"
                                                    tick={{ fontSize: 12 }}
                                                    width={100}
                                                    reversed
                                                />
                                                <Tooltip
                                                    formatter={(value) => [
                                                        `${value} min`,
                                                        'Tempo'
                                                    ]}
                                                    labelFormatter={(label) =>
                                                        `Zona: ${label}`
                                                    }
                                                />
                                                <Bar
                                                    dataKey="value"
                                                    radius={[5, 5, 5, 5]}
                                                    label={{
                                                        position: 'right',
                                                        formatter: (value) =>
                                                            `${value} min`,
                                                        fill: '#424242',
                                                        fontSize: 11
                                                    }}
                                                >
                                                    {(dataHrZone?.summaryZones || []).map(
                                                        (zone, index) => (
                                                            <Cell
                                                                key={`cell-${index}`}
                                                                fill={
                                                                    zone.color ||
                                                                    zoneColors[
                                                                        index %
                                                                            zoneColors.length
                                                                    ]
                                                                }
                                                            />
                                                        )
                                                    )}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </Box>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={8}>
                            <Card
                                elevation={3}
                                sx={{
                                    p: 2,
                                    borderRadius: 3,
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                    align="center"
                                    sx={{
                                        mb: 2,
                                        color: '#1976d2',
                                        position: 'relative',
                                        '&::after': {
                                            content: '""',
                                            display: 'block',
                                            width: '220px',
                                            height: '3px',
                                            backgroundColor: '#1976d2',
                                            borderRadius: '2px',
                                            margin: '6px auto 0'
                                        }
                                    }}
                                >
                                    {t('exercise.hrChart')}
                                </Typography>

                                <Box sx={{ width: '100%', height: 350 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={hrPoints}>
                                            {exerciseIntervals.map((interval, i) => {
                                                const color =
                                                    labelColorMap[interval.label];

                                                const x1 =
                                                    timeToIndex[interval.startTime];
                                                const x2 = timeToIndex[interval.endTime];

                                                if (x1 == null || x2 == null) return null;

                                                return (
                                                    <ReferenceArea
                                                        key={i}
                                                        x1={x1}
                                                        x2={x2}
                                                        strokeOpacity={0}
                                                        fill={color}
                                                        fillOpacity={0.15}
                                                    />
                                                );
                                            })}

                                            <XAxis
                                                dataKey="time"
                                                tick={{ fontSize: 10 }}
                                                interval="preserveStartEnd"
                                            />
                                            <YAxis
                                                domain={[
                                                    'auto',
                                                    Math.max(
                                                        event.config.hrMax,
                                                        event.config.hrTraining
                                                    ) + 4
                                                ]}
                                            />
                                            <Tooltip />
                                            <Line
                                                type="monotone"
                                                dataKey="hr"
                                                stroke="#1976d2"
                                                strokeWidth={2}
                                                dot={false}
                                            />
                                            <ReferenceLine
                                                y={event.config.hrMax}
                                                stroke="#f44336"
                                                strokeDasharray="4 4"
                                                label={{
                                                    value: 'FC Máx',
                                                    position: 'top',
                                                    fill: '#f44336',
                                                    fontSize: 10
                                                }}
                                            />
                                            <ReferenceLine
                                                y={event.config.hrTraining}
                                                stroke="#ffa000"
                                                strokeDasharray="3 3"
                                                label={{
                                                    value: 'FC Treino',
                                                    position: 'top',
                                                    fill: '#ffa000',
                                                    fontSize: 10
                                                }}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </Box>

                                {/* Legenda */}
                                {exerciseLegend.length > 0 && (
                                    <Box
                                        display="flex"
                                        flexWrap="wrap"
                                        gap={2}
                                        justifyContent="center"
                                    >
                                        {exerciseLegend.map((item) => (
                                            <Box
                                                key={item.label}
                                                display="flex"
                                                alignItems="center"
                                                gap={1}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 14,
                                                        height: 14,
                                                        borderRadius: 0.5,
                                                        backgroundColor: item.color,
                                                        opacity: 0.5
                                                    }}
                                                />
                                                <Box
                                                    sx={{
                                                        fontSize: 13,
                                                        color: 'text.secondary'
                                                    }}
                                                >
                                                    {item.label}
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Card>
                        </Grid>
                    </Grid>
                ) : event.type === 'questionnaire' ? (
                    <Typography variant="body1">
                        {t('exercise.questionnaireDetails')}
                    </Typography>
                ) : (
                    <Typography variant="body1">
                        {t('exercise.appointmentDetails')}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

EventDetailsCard.propTypes = {
    event: PropTypes.object,
    userData: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onPrev: PropTypes.func.isRequired,
    onNext: PropTypes.func.isRequired,
    selected: PropTypes.func.isRequired,
    selectedPlan: PropTypes.func.isRequired,
    expanded: PropTypes.func.isRequired
};

export default EventDetailsCard;
